import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import DomainDisabledRoundedIcon from '@mui/icons-material/DomainDisabledRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import type { SocietyRegistrationStatus, SocietyVerificationStatus } from '@/types/societyStatus';

export interface SocietyStatusBadgeProps {
  registrationStatus?: SocietyRegistrationStatus | string;
  verificationStatus?: SocietyVerificationStatus | string;
  variant?: 'chips' | 'stacked' | 'inline' | 'detailed' | 'row';
  size?: 'small' | 'medium';
  showHelpers?: boolean;
  sx?: object;
}

export const SocietyStatusBadge: React.FC<SocietyStatusBadgeProps> = ({
  registrationStatus = 'REGISTERED',
  verificationStatus = 'UNVERIFIED',
  variant = 'chips',
  size = 'small',
  showHelpers = false,
  sx = {},
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const regNorm: SocietyRegistrationStatus =
    String(registrationStatus).toUpperCase() === 'UNREGISTERED' ? 'UNREGISTERED' : 'REGISTERED';
  const verNorm: SocietyVerificationStatus =
    String(verificationStatus).toUpperCase() === 'VERIFIED' ? 'VERIFIED' : 'UNVERIFIED';

  const isRegistered = regNorm === 'REGISTERED';
  const isVerified = verNorm === 'VERIFIED';

  // Config for Registration status
  const regConfig = isRegistered
    ? {
        label: 'Registered',
        prefix: '▣',
        tooltip: 'Society registered with ApnaBlock',
        helper: 'Society registered',
        icon: <ApartmentRoundedIcon sx={{ fontSize: size === 'small' ? 14 : 16 }} />,
        color: 'primary' as const,
        bg: isDark ? 'rgba(59, 130, 246, 0.16)' : 'rgba(37, 99, 235, 0.08)',
        border: isDark ? 'rgba(59, 130, 246, 0.35)' : 'rgba(37, 99, 235, 0.25)',
        text: isDark ? '#93C5FD' : '#1D4ED8',
      }
    : {
        label: 'Unregistered',
        prefix: '□',
        tooltip: 'Registration not completed',
        helper: 'Registration not completed',
        icon: <DomainDisabledRoundedIcon sx={{ fontSize: size === 'small' ? 14 : 16 }} />,
        color: 'default' as const,
        bg: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.08)',
        border: isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.2)',
        text: isDark ? '#CBD5E1' : '#475569',
      };

  // Config for Verification status
  const verConfig = isVerified
    ? {
        label: 'Verified',
        prefix: '✓',
        tooltip: 'ApnaBlock verified society',
        helper: 'ApnaBlock verified',
        icon: <VerifiedRoundedIcon sx={{ fontSize: size === 'small' ? 14 : 16 }} />,
        color: 'success' as const,
        bg: isDark ? 'rgba(34, 197, 94, 0.16)' : 'rgba(22, 163, 74, 0.08)',
        border: isDark ? 'rgba(34, 197, 94, 0.35)' : 'rgba(22, 163, 74, 0.25)',
        text: isDark ? '#86EFAC' : '#15803D',
      }
    : {
        label: 'Unverified',
        prefix: '◌',
        tooltip: 'Verification pending / not completed',
        helper: 'Verification pending / not completed',
        icon: <HourglassEmptyRoundedIcon sx={{ fontSize: size === 'small' ? 14 : 16 }} />,
        color: 'warning' as const,
        bg: isDark ? 'rgba(245, 158, 11, 0.14)' : 'rgba(217, 119, 6, 0.08)',
        border: isDark ? 'rgba(245, 158, 11, 0.35)' : 'rgba(217, 119, 6, 0.25)',
        text: isDark ? '#FCD34D' : '#B45309',
      };

  if (variant === 'stacked') {
    return (
      <Stack spacing={0.6} sx={{ alignItems: 'flex-start', ...sx }}>
        <Tooltip title={regConfig.tooltip} arrow placement="top">
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1,
              py: 0.25,
              borderRadius: '6px',
              bgcolor: regConfig.bg,
              border: `1px solid ${regConfig.border}`,
              color: regConfig.text,
              fontSize: size === 'small' ? '0.725rem' : '0.8125rem',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {regConfig.icon}
            <span>{regConfig.prefix} {regConfig.label}</span>
          </Box>
        </Tooltip>
        <Tooltip title={verConfig.tooltip} arrow placement="top">
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1,
              py: 0.25,
              borderRadius: '6px',
              bgcolor: verConfig.bg,
              border: `1px solid ${verConfig.border}`,
              color: verConfig.text,
              fontSize: size === 'small' ? '0.725rem' : '0.8125rem',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {verConfig.icon}
            <span>{verConfig.prefix} {verConfig.label}</span>
          </Box>
        </Tooltip>
        {showHelpers && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
            {regConfig.helper} · {verConfig.helper}
          </Typography>
        )}
      </Stack>
    );
  }

  if (variant === 'detailed') {
    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          ...sx,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
              Registration Status
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <Box sx={{ color: regConfig.text, display: 'flex' }}>{regConfig.icon}</Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: regConfig.text }}>
                {regConfig.prefix} {regConfig.label}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {regConfig.helper}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
              Verification Status
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <Box sx={{ color: verConfig.text, display: 'flex' }}>{verConfig.icon}</Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: verConfig.text }}>
                {verConfig.prefix} {verConfig.label}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {verConfig.helper}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  if (variant === 'row') {
    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ flexWrap: 'wrap', gap: 0.75, ...sx }}
      >
        <Tooltip title={regConfig.tooltip} arrow>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.85,
              py: 0.2,
              borderRadius: '5px',
              bgcolor: regConfig.bg,
              border: `1px solid ${regConfig.border}`,
              color: regConfig.text,
              fontSize: size === 'small' ? '0.7rem' : '0.775rem',
              fontWeight: 600,
            }}
          >
            {regConfig.icon}
            <span>{regConfig.prefix} {regConfig.label}</span>
          </Box>
        </Tooltip>
        <Tooltip title={verConfig.tooltip} arrow>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.85,
              py: 0.2,
              borderRadius: '5px',
              bgcolor: verConfig.bg,
              border: `1px solid ${verConfig.border}`,
              color: verConfig.text,
              fontSize: size === 'small' ? '0.7rem' : '0.775rem',
              fontWeight: 600,
            }}
          >
            {verConfig.icon}
            <span>{verConfig.prefix} {verConfig.label}</span>
          </Box>
        </Tooltip>
      </Stack>
    );
  }

  // Default 'chips' variant
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{ flexWrap: 'wrap', gap: 0.5, ...sx }}
    >
      <Tooltip title={regConfig.tooltip} arrow>
        <Chip
          size={size}
          icon={regConfig.icon}
          label={`${regConfig.prefix} ${regConfig.label}`}
          variant="outlined"
          sx={{
            fontWeight: 600,
            bgcolor: regConfig.bg,
            borderColor: regConfig.border,
            color: regConfig.text,
            '& .MuiChip-icon': {
              color: 'inherit',
            },
          }}
        />
      </Tooltip>
      <Tooltip title={verConfig.tooltip} arrow>
        <Chip
          size={size}
          icon={verConfig.icon}
          label={`${verConfig.prefix} ${verConfig.label}`}
          variant="outlined"
          sx={{
            fontWeight: 600,
            bgcolor: verConfig.bg,
            borderColor: verConfig.border,
            color: verConfig.text,
            '& .MuiChip-icon': {
              color: 'inherit',
            },
          }}
        />
      </Tooltip>
    </Stack>
  );
};

export default SocietyStatusBadge;
